import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useRecipesBoardId } from './recipes_hooks';
import RecipeBoardActionsPopupMenu from './recipe_board_actions_popup_menu';
import RecipesBoardEditModal from './recipes_board_edit_modal';
import { ActivityObjectType, ContainerType } from '../../API';
import {
  useDeleteMealMutation,
  useDeleteSmorgBoardMutation,
  useGetSharedProgrammesQuery,
  useGetSpaceQuery,
  useUpdateSmorgBoardMutation,
  useUpdateSpaceMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  useGetEndUserSpaceQuery,
  useGetOwnedRecipesBoardsQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { removeRecipesBoard } from '../../operations/recipes_operations';
import { userIsCreator } from '../../services/users';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';
import { sharedProgrammesWhichReferenceMeals } from '../../services/programmes';

const TopNavBarMobileRecipes = ({
  recipesBoardSelectorHidden,
  popupVisible,
  openInRenameMode,
}) => {
  const [recipesBoardEditMode, setRecipesBoardEditMode] =
    useState(openInRenameMode);

  const selectedRecipesBoardId = useRecipesBoardId();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: ownedRecipesBoards } = useGetOwnedRecipesBoardsQuery();

  const [deleteMeal] = useDeleteMealMutation();

  const [deleteSmorgBoard] = useDeleteSmorgBoardMutation();

  const selectedRecipesBoard = (ownedRecipesBoards || []).find(
    (b) => b.id === selectedRecipesBoardId,
  );

  const isRecipesBoardEmbeddedInProgramme =
    selectedRecipesBoard?.embeddedInContainerType === ContainerType.PROGRAMME &&
    !!selectedRecipesBoard?.embeddedInContainerID;

  const { data: userProfile } = useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: space } = useGetEndUserSpaceQuery(undefined, {
    skip: !userProfile || isSmorgStudio,
  });

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const { data: healthProSpace } = useGetSpaceQuery();

  const [updateSmorgBoard] = useUpdateSmorgBoardMutation();

  const [updateSpace] = useUpdateSpaceMutation();

  const spaceAssignedSharedBoardIDs = space?.sharedRecipesBoardIDs || [];

  const isProductizedSharedBoard = spaceAssignedSharedBoardIDs.includes(
    selectedRecipesBoardId,
  );

  const onEditRecipesBoard = () => {
    setRecipesBoardEditMode(true);
  };

  /** TODO duplicate code with RecipesNavigationBar */
  const updateRecipesBoard = async (updatedRecipesBoard) => {
    setRecipesBoardEditMode(false);
    await updateSmorgBoard(updatedRecipesBoard);
    if (isSmorgStudio) {
      reportAccountActivitySignalOperation({
        spaceID: space.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.RECIPES,
        lastActivityObjectID: updatedRecipesBoard.id,
      });
    }
    // Remove the ?rename from URL
    navigate(`/recipes/${selectedRecipesBoardId}`);
  };

  /** TODO duplicate code with RecipesNavigationBar */
  const onDuplicateRecipesBoard = () => {
    console.log('onDuplicateRecipesBoard');
    // TODO implement
  };

  /** TODO duplicate code with RecipesNavigationBar */
  const onShareRecipesBoard = () => {
    console.log('onShareRecipesBoard');
    navigate(`/recipes/${selectedRecipesBoardId}/share`);
  };

  /** TODO duplicate code with RecipesNavigationBar */
  const onDeleteRecipesBoard = () => {
    const recipesBoard = ownedRecipesBoards.find(
      (b) => b.id === selectedRecipesBoardId,
    );
    const firstRemainingRecipesBoard = ownedRecipesBoards.find(
      (b) => b.id !== selectedRecipesBoardId,
    );
    removeRecipesBoard(
      recipesBoard,
      healthProSpace,
      deleteMeal,
      deleteSmorgBoard,
      updateSpace,
      dispatch,
    );
    if (isSmorgStudio) {
      reportAccountActivitySignalOperation({
        spaceID: healthProSpace.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.RECIPES,
        lastActivityObjectID: recipesBoard.id,
      });
    }
    navigate(`/recipes/${firstRemainingRecipesBoard.id}`);
  };

  const isSharedProgrammeBoard = [
    ContainerType.PROGRAMME,
    ContainerType.SHARED_PROGRAMME,
  ].includes(selectedRecipesBoard?.embeddedInContainerType);

  const { data: sharedProgrammes } = useGetSharedProgrammesQuery(
    healthProSpace?.id,
    {
      skip: !healthProSpace,
    },
  );

  const mealIDs = (selectedRecipesBoard?.menus || []).flatMap(
    (menu) => menu.mealIDs,
  );

  const sharedProgrammesLinked = sharedProgrammesWhichReferenceMeals(
    mealIDs,
    sharedProgrammes || [],
  );

  return (
    <>
      <div className={classNames('topNavBarMobile', { popupVisible })}>
        <div className="screen-title" style={{ flexGrow: 1 }}>
          Recipes
        </div>
        {!recipesBoardSelectorHidden && (
          <div style={{ margin: 'auto', marginRight: 10 }}>
            <RecipeBoardActionsPopupMenu
              sharedProgrammesLinked={sharedProgrammesLinked}
              onEditRecipesBoard={onEditRecipesBoard}
              onDuplicateRecipesBoard={onDuplicateRecipesBoard}
              onShareRecipesBoard={onShareRecipesBoard}
              onDeleteRecipesBoard={onDeleteRecipesBoard}
              editBoardDisabled={
                isRecipesBoardEmbeddedInProgramme ||
                isSharedProgrammeBoard ||
                isProductizedSharedBoard
              }
              duplicateBoardDisabled={
                isRecipesBoardEmbeddedInProgramme ||
                isSharedProgrammeBoard ||
                isProductizedSharedBoard
              }
              shareBoardDisabled={
                isRecipesBoardEmbeddedInProgramme ||
                isSharedProgrammeBoard ||
                isProductizedSharedBoard
              }
              deleteBoardDisabled={
                isRecipesBoardEmbeddedInProgramme ||
                isSharedProgrammeBoard ||
                !ownedRecipesBoards ||
                ownedRecipesBoards.length <= 1 ||
                selectedRecipesBoard?.isMyFavouritesBoard
              }
              onPublishRecipesBoardToSpace={() => {}}
              onUnpublishRecipesBoardFromSpace={() => {}}
            />
          </div>
        )}
      </div>
      {recipesBoardEditMode && (
        <RecipesBoardEditModal
          initialBoard={selectedRecipesBoard}
          onSave={updateRecipesBoard}
          onDismiss={() => setRecipesBoardEditMode(false)}
        />
      )}
    </>
  );
};

TopNavBarMobileRecipes.propTypes = {
  recipesBoardSelectorHidden: PropTypes.bool,
  popupVisible: PropTypes.bool,
  openInRenameMode: PropTypes.bool,
};

TopNavBarMobileRecipes.defaultProps = {
  recipesBoardSelectorHidden: false,
  popupVisible: false,
  openInRenameMode: false,
};

export default TopNavBarMobileRecipes;
